// Единая точка выхода наружу.
//
// Любой уход из мини-аппа (задание, спонсор, подарок, рулетка, оффер) сворачивает
// приложение. Именно это окно и монетизируется: скрипт совместимости держит документ
// «видимым», поэтому цепочка Adsgram продолжает крутиться, пока пользователь в другом
// чате. Но работает это только если к моменту сворачивания цепочка реально запущена
// и открыто окно визита — раньше и то и другое делала одна кнопка на двух экранах,
// а все остальные ссылки уходили «в тишину».
//
// Здесь ссылка открывается всегда и сразу: реклама не должна задерживать переход,
// поэтому цепочку мы только запускаем (или подключаемся к уже идущей), не дожидаясь её.

import { logAd } from './adDiagnostics';
import { isAdsgramSequenceActive, startAdsgramSequence } from './adsgramFlow';
import { markOfferVisit } from './offerVisit';

// Подключиться к уже идущей цепочке бесплатно, а вот НАЧИНАТЬ новую на каждый тап
// нельзя: список заданий — это десяток ссылок подряд, и полноэкранный показ на
// каждую превратился бы в издевательство (а AdsGram всё равно ушёл бы в no-fill).
const NAVIGATION_DEDUPLICATION_MS = 750;
// postingBot gives Adsgram a short foreground window after onStart before
// opening a bot/link. Without it Telegram can freeze the Mini App before the
// fullscreen session has fully attached.
export const INTERSTITIAL_START_SETTLE_MS = 250;
let lastNavigation: { url: string; at: number } | null = null;
let interceptionInstalled = false;

interface NavigationHandoff {
  onUnavailable?: () => void;
  onFinished?: () => void;
}

/** Приводит ссылку к виду, который понимает Telegram.WebApp. */
export function normalizeExternalUrl(rawLink: string): string {
  if (!rawLink) return '';
  let url = String(rawLink).trim();

  if (!url.startsWith('http://') && !url.startsWith('https://') && url.startsWith('t.me/')) {
    url = `https://${url}`;
  }

  if (url.startsWith('tg://resolve?')) {
    try {
      const domain = new URL(url).searchParams.get('domain');
      if (domain) url = `https://t.me/${domain}`;
    } catch {
      // Оставляем как есть — ниже отработает штатный опенер.
    }
  }

  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('tg://')) {
    if (url.includes('.')) url = `https://${url}`;
  }

  return url;
}

/** Сам переход. Внутри Telegram — только через WebApp API, иначе окно не откроется. */
export function openExternalUrlRaw(url: string): void {
  try {
    const tg = (window as any).Telegram?.WebApp;
    if (tg) {
      if (url.includes('t.me/') && typeof tg.openTelegramLink === 'function') {
        tg.openTelegramLink(url);
        return;
      }
      if (typeof tg.openLink === 'function') {
        tg.openLink(url);
        return;
      }
    }
  } catch {
    // Ниже — браузерный запасной вариант.
  }
  window.open(url, '_blank');
}

export function openGiftHandoffUrl(url: string): void {
  try {
    window.location.assign(url);
  } catch {
    openExternalUrlRaw(url);
  }
}

/**
 * Common pre-navigation work. Explicit app links use the optional handoff to
 * delay navigation until interstitial terminal; an external notification only
 * starts or joins the chain.
 */
function beginExternalNavigation(
  rawLink: string | null,
  trigger: string,
  handoff?: NavigationHandoff,
): boolean {
  const url = rawLink === null ? '' : normalizeExternalUrl(rawLink);
  if (rawLink !== null && !url) return false;

  const now = Date.now();
  if (url && lastNavigation && lastNavigation.url === url && now - lastNavigation.at < NAVIGATION_DEDUPLICATION_MS) {
    return true;
  }
  if (url) lastNavigation = { url, at: now };

  // Порядок важен: окно визита и цепочка должны существовать ДО сворачивания,
  // иначе стартовый цикл посчитает скрытое окно поводом остановиться.
  markOfferVisit(trigger);

  const joining = isAdsgramSequenceActive();
  const launched = startAdsgramSequence({
    trigger,
    onInterstitialUnavailable: handoff?.onUnavailable,
    onInterstitialFinished: handoff?.onFinished,
  });
  if (!launched) handoff?.onUnavailable?.();
  logAd('info', 'link', `переход: ${trigger}`, { url, adsLaunched: launched, joined: joining });
  return true;
}

/**
 * Retained for integrations that emit an external-navigation event themselves.
 */
export function installExternalLinkInterception(): void {
  if (interceptionInstalled) return;
  interceptionInstalled = true;

  window.addEventListener('miniapp:external-navigation', (event: Event) => {
    const detail = (event as CustomEvent<{ url?: unknown; source?: unknown }>).detail;
    const url = typeof detail?.url === 'string' ? detail.url : '';
    if (!url) return;
    const source = typeof detail?.source === 'string' ? detail.source : 'unknown';
    beginExternalNavigation(url, `external_${source}`);
  });
}

/**
 * Открывает внешнюю ссылку и переводит уход пользователя в фоновый показ рекламы.
 *
 * Переход происходит после старта interstitial либо сразу при его недоступности.
 */
export function openExternalLink(rawLink: string | Promise<string>, trigger: string, useLocationAssign = true): boolean {
  const immediateUrl = typeof rawLink === 'string' ? normalizeExternalUrl(rawLink) : null;
  if (immediateUrl === '') return false;
  const resolvedUrl = Promise.resolve(rawLink)
    .then(normalizeExternalUrl)
    .catch(() => '');

  let opened = false;
  const openAfter = (delayMs: number) => {
    if (opened) return;
    opened = true;
    window.setTimeout(() => {
      void resolvedUrl.then((url) => {
        if (url) (useLocationAssign ? openGiftHandoffUrl : openExternalUrlRaw)(url);
      });
    }, delayMs);
  };

  return beginExternalNavigation(immediateUrl, trigger, {
    onUnavailable: () => openAfter(0),
    onFinished: () => openAfter(INTERSTITIAL_START_SETTLE_MS),
  });
}
